class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper"
    };

    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content"
    };

    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;
    this.currentPage = 1;
    this.paginationType;
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;
    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5
    };
    this.enableFilters;
    this.enableSorting;
    this.filtersType;
    this.filtersPosition;
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;

    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());
    this.handleStickyToolbar();

    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();

    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (event, element) => this.toggleView(Number(element.dataset.column))
    });

    this.initLoadMore();

    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;

    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (event) => {
      if (event.target === sidebar) {
        this.closeSidebarFilter();
      }
    });
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;

    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (event) => {
      if (event.target === sortingWrapper) {
        this.closeMobileSorting();
      }
    });
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => {
      sortingWrapper.style.setProperty("display", "none");
    }, 300);
  };

  initAccordions = () => {
    if (this.accordions) {
      this.accordions.destroy();
    }

    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty("opacity", "1")
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      if (this.accordions) {
        this.accordions.setContentHeight();
      }
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");

    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => {
        sidebar.style.removeProperty("display");
      }, 300);
    }
  };

  toggleView = (columns) => {
    if (!columns || this.view === "search") return;

    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;

    const responsiveImages = productContainer.querySelectorAll("responsive-image");
    if (responsiveImages && responsiveImages.length > 0) {
      responsiveImages.forEach(image => {
        image.disconnectedCallback();
        image.connectedCallback();
      });
    }
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return;

    const device = this.getDeviceByScreenSize();
    const maxCols = this.MAX_COL_BY_SCREEN_SIZE[device];

    if (maxCols < this.activeCol) {
      this.toggleView(maxCols);
    }
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.intersectionRatio === 1) {
            this.loadMoreProducts();
          }
        });
      }, { threshold: 1 });
      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);

    fetchSection(this.sectionId, {
      fromCache: true,
      params: { page: nextPage }
    })
      .then(response => {
        response.querySelectorAll("[data-product-container] .m-product-item").forEach(item => {
          productContainer.appendChild(item);
        });
        this.initProducts();
      })
      .catch(error => {})
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;

        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          if (this.infiniteLoadingObserver) {
            this.infiniteLoadingObserver.unobserve(loadMoreBtn);
          }
        }
      });
  };

  initProducts = () => {
    if (MinimogTheme.CompareProduct) {
      MinimogTheme.CompareProduct.setCompareButtonsState();
    }
    if (MinimogTheme.Wishlist) {
      MinimogTheme.Wishlist.setWishlistButtonsState();
    }
  };

  toggleLoading = (isLoading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const action = isLoading ? "add" : "remove";
      loadMoreBtn.classList[action]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    const toolbarOffset = toolbar && toolbar.offsetTop;
    let lastScrollY = 0;

    window.addEventListener("scroll", () => {
      if (MinimogTheme.config.mqlMobile) {
        const scrollY = window.scrollY;

        if (scrollY <= toolbarOffset) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }

        toolbar.classList.add("m-collection-toolbar--sticky");

        if (scrollY > toolbar.offsetHeight + toolbarOffset && scrollY > lastScrollY && !toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (scrollY < lastScrollY && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }

        lastScrollY = scrollY;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    });
  }
}
